<template>
  <div
    class="app-tile"
    :class="{
      'app-tile_disabled': !userHasPermission || appStore.isNavigating,
    }"
  >
    <router-link
      :to="{
        name: appListMap[app.appId].route as any,
      }"
      class="app-tile__link"
      :style="`border-color: ${appTileColorMap[appStore.getAppGroup(app)]};`"
    >
      <div class="app-tile__content">
        <component
          :is="appListMap[app.appId].icon"
          class="app-tile__content-icon"
        />
        <div class="app-tile__content-text">
          <div
            class="typo-font-3"
            style="color: var(--color-224);"
          >
            {{ app.displayName }}
          </div>
          <div
            class="typo-small"
            style="color: var(--color-text-secondary);"
          >
            {{ i18n.global.t(`api.AppGroupCommercial.${app.appGroupCommercial}`) }}
          </div>
        </div>
        <PrmTag
          v-if="doesExpireSoon"
          v-tooltip="{
            value: doesExpireSoon && appLicenseExpiryDayjs ? i18n.global.t(`${I18N_PREFIX}.expiresSoon`, {
              expiryDate: formatDayjsDate(appLicenseExpiryDayjs),
              appName: app.displayName,
            }) : undefined,
          }"
          severity="warning"
          icon="pi pi-exclamation-triangle"
        />
      </div>
    </router-link>

    <PrmButton
      v-if="userHasPermission"
      type="button"
      text
      severity="secondary"
      class="app-tile__pin-button"
      :disabled="customUserStore.customPinnedAppMapIsLoading"
      @hover.capture.stop
      @click.capture.stop="customUserStore.toggleCustomPinnedApp(app.appId)"
    >
      <template #icon>
        <PinOnIcon
          v-if="isAppPinned"
          style="color: var(--icon-icon-action);"
          class="app-tile__pin-icon"
        />
        <PinOffIcon
          v-else
          style="color: var(--icon-icon-action);"
          class="app-tile__pin-icon"
        />
      </template>
    </PrmButton>
  </div>
</template>

<script lang="ts" setup>
import PrmButton from '@NitrexGroup/primevue/button';
import PrmTag from '@NitrexGroup/primevue/tag';
import dayjs from '@dayjs';
import {
  i18n,
} from '@i18n';
import {
  isNullish,
} from '@qmulus/ui/utils/common.ts';
import {
  computed,
} from 'vue';

import PinOffIcon from '@/assets/icon/pin-off.svg?component';
import PinOnIcon from '@/assets/icon/pin-on.svg?component';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  useCustomUserStorageStore,
} from '@/store/pinia/custom-user-storage.ts';
import {
  useLicenseStore,
} from '@/store/pinia/license.ts';
import {
  EAppId,
  TApp,
} from '@/types/app-type.ts';
import {
  appListMap,
} from '@/types/app-util.ts';
import {
  appTileColorMap,
} from '@/utils/app-tile.ts';
import {
  formatDayjsDate,
} from '@/utils/date-formats.ts';

const I18N_PREFIX = 'components/app-tile/app-tile';

const appStore = useAppStore();
appStore.loadCurrentUser();
const customUserStore = useCustomUserStorageStore();
customUserStore.loadCustomPinnedAppMap();
const licenseStore = useLicenseStore();

const props = defineProps<{
  app: TApp,
}>();

const isAppPinned = computed(() => customUserStore.customPinnedAppMap?.[props.app.appId]);
const userHasPermission = computed(() => {
  // Settings app contains restricted and unrestricted sections and has appID TENANT_SETTINGS
  // user should be able to always access unrestricted section (user settings)
  // thus we create an exception for this appID
  if (props.app.appId === EAppId.TENANT_SETTINGS) {
    return true;
  }
  return !!appStore.user?.appPermissions?.[props.app.appId];
});

const appLicense = computed(() => {
  if (isNullish(licenseStore.license)) {
    return undefined;
  }
  return licenseStore.license.apps.find((appLicense) => appLicense.appId === props.app.appId);
});

const appLicenseExpiryDayjs = computed(() => {
  if (isNullish(appLicense.value)) {
    return undefined;
  }
  if (appLicense.value.expiry === 'unlimited') {
    return undefined;
  }
  return dayjs(appLicense.value.expiry);
});

const doesExpireSoon = computed(() => {
  if (isNullish(appLicenseExpiryDayjs.value) || isNullish(appLicense.value)) {
    return false;
  }
  const showWarningAfter = appLicenseExpiryDayjs.value.subtract(appLicense.value.expiryWarningDays || 0, 'days');
  return dayjs().isAfter(showWarningAfter);
});
</script>

<style lang="scss" scoped>
@use "@qmulus/ui/styles/color";

$horz-gap: var(--gap-union);

.app-tile {
  position: relative;
  width: 100%;
  height: 3.875rem;
  border-radius: 0.25rem;
  overflow: hidden;
  border: 1px solid var(--color-103);
  display: flex;
  justify-content: space-between;

  &_disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.5;
  }

  &__link {
    display: flex;
    padding: var(--gap);
    justify-content: space-between;
    border-left: 0.75rem solid;
    flex-grow: 1;
  }

  &__pin-button {
    padding: var(--gap);
    color: var(--color-232);
  }

  &__content {
    height: 100%;
    display: flex;
    align-items: center;
    gap: $horz-gap;
  }

  &__content-icon {
    width: 1.875rem;
    height: 1.875rem;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--color-224);
  }

  &__content-text {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__pin-icon {
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
